import React from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  keyframes,
  useTheme,
} from "@mui/material";
import {
  AddOutlined,
  ArrowBackOutlined,
  AttachFileOutlined,
  AutoAwesomeOutlined,
  BrushOutlined,
  CheckRounded,
  CloseOutlined,
  ContentCopyOutlined,
  DownloadOutlined,
  ImageOutlined,
  KeyboardArrowDownRounded,
  MicNoneOutlined,
  MicOffOutlined,
  RefreshOutlined,
  SearchOutlined,
  SendRounded,
  SettingsOutlined,
  ZoomInOutlined,
} from "@mui/icons-material";
import { GlobalContext } from "../../../context/GlobalContext";
import { UserContext } from "../../../context/UserContext";
import { mediaSrc } from "../../../utils/mediaSrc";
import {
  POYO_ASPECT_RATIOS,
  POYO_TXT2IMG_MODELS,
  DEFAULT_POYO_TXT2IMG_MODEL,
  DEFAULT_ASPECT_RATIO,
  autoNameFromPrompt,
} from "../../../constants/poyoTxt2imgModels";
import { cleanImageModelLabel } from "./CharacterAiModelSelect";
import {
  IMAGE_GENERATION_SAYINGS,
  nextImageSayingIndex,
} from "../imageGenerationSayings";
import MentionPromptInput from "../../videoStudio/components/MentionPromptInput";
import {
  MAX_IMAGE_STUDIO_REFS,
  collectStudioMentionAssets,
  buildImageStudioApiPrompt,
  promptWithVisibleMentions,
  labelFromFilename,
  durableReferencePreviews,
} from "../imageStudioRefs";
import {
  fetchInfModels,
  fetchInfModelStatus,
  invalidateInfModelsCache,
} from "../infModelsApi";

const AUTO_MODEL_ID = "auto";
/** Best / latest image model used when Auto is selected. */
export const AUTO_RESOLVES_TO = DEFAULT_POYO_TXT2IMG_MODEL;

const THUMB_MAX = 260;
const SAYING_ROTATE_MS = 3500;
const CHAT_PAGE_SIZE = 5;
/** Max turns kept in memory from gallery history. */
const MAX_CHAT_HISTORY_TURNS = 60;

function absoluteMediaUrl(url) {
  const s = mediaSrc(url) || String(url || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (typeof window === "undefined") return s;
  const path = s.startsWith("/") ? s : `/${s}`;
  return `${window.location.origin}${path}`;
}

function historyRowToTurn(item) {
  const imageUrl = mediaSrc(item.photo_url || item.photoUrl);
  const data = item.data && typeof item.data === "object" ? item.data : null;
  const hosted = Array.isArray(data?.reference_image_urls)
    ? data.reference_image_urls
    : Array.isArray(item.reference_image_urls)
      ? item.reference_image_urls
      : [];
  const client = Array.isArray(data?.client_reference_previews)
    ? data.client_reference_previews
    : Array.isArray(item.client_reference_previews)
      ? item.client_reference_previews
      : [];
  // Prefer hosted HTTPS refs; never keep revoked blob: URLs.
  const refUrls = (hosted.length ? hosted : client).filter(
    (u) => u && !String(u).startsWith("blob:"),
  );
  return {
    id: `hist_${item.id}`,
    prompt: String(item.prompt || item.name || "Image").trim() || "Image",
    status: "done",
    imageUrl,
    jobId: item.id,
    error: null,
    fromHistory: true,
    createdAt: item.created_at || item.createdAt || null,
    referencePreviews: refUrls.map((u) => mediaSrc(u) || u).filter(Boolean),
  };
}

function sortHistoryOldestFirst(rows) {
  return [...(rows || [])].sort((a, b) => {
    const ta = Date.parse(a.created_at || a.createdAt || "") || Number(a.id) || 0;
    const tb = Date.parse(b.created_at || b.createdAt || "") || Number(b.id) || 0;
    return ta - tb;
  });
}

/** Merge gallery rows into the chat log (oldest → newest), keeping live processing turns. */
function mergeTurnsWithHistory(prevTurns, historyRows) {
  const sorted = sortHistoryOldestFirst(historyRows).filter(
    (r) => r.status === "active" && (r.photo_url || r.photoUrl),
  );
  const limited = sorted.slice(Math.max(0, sorted.length - MAX_CHAT_HISTORY_TURNS));
  const byJobId = new Map();
  for (const row of limited) {
    const turn = historyRowToTurn(row);
    if (turn.imageUrl) byJobId.set(Number(row.id), turn);
  }

  const live = [];
  for (const t of prevTurns || []) {
    if (t.status === "processing" || t.status === "error") {
      live.push(t);
      continue;
    }
    const jid = t.jobId != null ? Number(t.jobId) : null;
    if (jid && byJobId.has(jid)) {
      // Keep local prompt text if richer; prefer done image from either source.
      const hist = byJobId.get(jid);
      byJobId.set(jid, {
        ...hist,
        prompt: t.prompt || hist.prompt,
        imageUrl: t.imageUrl || hist.imageUrl,
        status: "done",
        fromHistory: hist.fromHistory,
        referencePreviews:
          (Array.isArray(t.referencePreviews) && t.referencePreviews.length
            ? t.referencePreviews
            : hist.referencePreviews) || [],
      });
      continue;
    }
    // Seed / session turns not yet in gallery
    if (!jid || !byJobId.has(jid)) {
      live.push(t);
    }
  }

  return [...byJobId.values(), ...live];
}

const loaderAura = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(0.96); }
  50% { opacity: 1; transform: scale(1.06); }
`;

const loaderRing = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const loaderPulse = keyframes`
  0%, 100% {
    box-shadow:
      0 0 16px rgba(56, 189, 248, 0.18),
      0 0 36px rgba(251, 146, 60, 0.1);
  }
  50% {
    box-shadow:
      0 0 28px rgba(56, 189, 248, 0.42),
      0 0 58px rgba(251, 146, 60, 0.26);
  }
`;

function ImageCreatingPlaceholder({ lang }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const [index, setIndex] = React.useState(() =>
    Math.floor(Math.random() * IMAGE_GENERATION_SAYINGS.length),
  );

  React.useEffect(() => {
    setIndex(Math.floor(Math.random() * IMAGE_GENERATION_SAYINGS.length));
    const id = window.setInterval(() => {
      setIndex((prev) => nextImageSayingIndex(prev));
    }, SAYING_ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  const sky = theme.palette.primary.main || "#38bdf8";
  const warm = theme.palette.warning?.main || "#fb923c";

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: THUMB_MAX,
        animation: `${loaderPulse} 2.5s ease-in-out infinite`,
        borderRadius: 2.75,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: { xs: -10, sm: -14 },
          borderRadius: 4,
          background: `radial-gradient(ellipse at 20% 10%, ${alpha(sky, 0.55)}, transparent 55%),
            radial-gradient(ellipse at 90% 95%, ${alpha(warm, 0.42)}, transparent 50%)`,
          filter: "blur(18px)",
          animation: `${loaderAura} 2.6s ease-in-out infinite`,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          borderRadius: 2.75,
          p: "2px",
          overflow: "hidden",
          isolation: "isolate",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: "-45%",
            background: `conic-gradient(
              from 0deg,
              ${alpha(sky, 0.08)},
              ${sky},
              ${warm},
              ${alpha(sky, 0.1)},
              ${sky}
            )`,
            animation: `${loaderRing} 2.8s linear infinite`,
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            aspectRatio: "3 / 4",
            borderRadius: 2.4,
            overflow: "hidden",
            bgcolor: dark ? alpha("#0b1220", 0.97) : alpha("#fff", 0.97),
            backgroundImage: `
              radial-gradient(${alpha(theme.palette.text.primary, dark ? 0.12 : 0.08)} 1px, transparent 1px),
              linear-gradient(155deg, ${alpha(sky, dark ? 0.14 : 0.09)} 0%, transparent 45%, ${alpha(warm, dark ? 0.1 : 0.07)} 100%)
            `,
            backgroundSize: "14px 14px, 100% 100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            p: 1.75,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: sky,
                boxShadow: `0 0 12px ${alpha(sky, 0.95)}`,
                animation: `${loaderAura} 1.5s ease-in-out infinite`,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                letterSpacing: 0.3,
              }}
            >
              {lang?.imageStudioCreatingLabel || "Creating image"}
            </Typography>
          </Stack>
          <Box
            sx={{
              flex: 1,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 1,
            }}
          >
            <Typography
              key={index}
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                fontStyle: "italic",
                lineHeight: 1.5,
                maxWidth: 200,
                animation: "fadeSay 0.45s ease",
                "@keyframes fadeSay": {
                  from: { opacity: 0, transform: "translateY(4px)" },
                  to: { opacity: 1, transform: "none" },
                },
              }}
            >
              {IMAGE_GENERATION_SAYINGS[index]}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.35); }
  50% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
`;

function stripVendorNames(text) {
  return String(text || "")
    .replace(/\bPoYo\b/gi, "")
    .replace(/\bKie\.?\w*\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function AspectIcon({ ratio, selected }) {
  const theme = useTheme();
  const [w, h] = String(ratio || "1:1").split(":").map(Number);
  const max = 22;
  const scale = max / Math.max(w || 1, h || 1);
  return (
    <Box
      sx={{
        width: Math.max(8, Math.round((w || 1) * scale)),
        height: Math.max(8, Math.round((h || 1) * scale)),
        borderRadius: 0.5,
        border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.text.secondary}`,
        bgcolor: alpha(theme.palette.text.primary, selected ? 0.12 : 0.04),
        flexShrink: 0,
      }}
    />
  );
}

function sortHistoryNewestFirst(rows) {
  return [...(rows || [])].sort((a, b) => {
    const ta = Date.parse(a.created_at || a.createdAt || "") || Number(a.id) || 0;
    const tb = Date.parse(b.created_at || b.createdAt || "") || Number(b.id) || 0;
    return tb - ta;
  });
}

const HISTORY_PAGE_SIZE = 24;

/**
 * Right-rail generation history: fixed height, scrollable 2-col grid, lazy page load.
 */
function GenerationHistoryPanel({
  history,
  lang,
  theme,
  dark,
  selectedUrls,
  onSelect,
}) {
  const items = React.useMemo(
    () =>
      sortHistoryNewestFirst(history).filter(
        (r) => r.status === "active" && (r.photo_url || r.photoUrl),
      ),
    [history],
  );
  const [visibleCount, setVisibleCount] = React.useState(HISTORY_PAGE_SIZE);
  const scrollRef = React.useRef(null);
  const sentinelRef = React.useRef(null);

  React.useEffect(() => {
    setVisibleCount(HISTORY_PAGE_SIZE);
  }, [items.length]);

  React.useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || visibleCount >= items.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((n) => Math.min(n + HISTORY_PAGE_SIZE, items.length));
        }
      },
      { root, rootMargin: "160px 0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [items.length, visibleCount]);

  const visible = items.slice(0, visibleCount);

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2.5,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: dark ? alpha("#fff", 0.03) : alpha("#000", 0.02),
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 1.5,
          py: 1.15,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle2" fontWeight={800} letterSpacing={-0.2}>
          {lang?.imageStudioHistory || "Generation history"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {items.length
            ? `${items.length} ${lang?.imageStudioHistoryCount || "images"}`
            : lang?.imageStudioHistoryEmpty || "Generated images appear here"}
        </Typography>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          p: 1.25,
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: alpha(theme.palette.text.primary, 0.18),
            borderRadius: 99,
          },
        }}
      >
        {!items.length ? (
          <Box
            sx={{
              py: 6,
              px: 1,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <ImageOutlined sx={{ fontSize: 28, opacity: 0.45, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {lang?.imageStudioHistoryEmptyHint ||
                "Your Image Studio generations will show up in this grid."}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 0.75,
              }}
            >
              {visible.map((item) => {
                const url = mediaSrc(item.photo_url || item.photoUrl);
                const abs = absoluteMediaUrl(item.photo_url || item.photoUrl);
                if (!url) return null;
                const selected =
                  (selectedUrls &&
                    (selectedUrls.has(url) || selectedUrls.has(abs))) ||
                  false;
                return (
                  <Box
                    key={item.id}
                    component="button"
                    type="button"
                    onClick={() => onSelect?.(item, { url, abs })}
                    title={item.name || item.prompt || ""}
                    sx={{
                      all: "unset",
                      cursor: "pointer",
                      position: "relative",
                      aspectRatio: "1 / 1",
                      borderRadius: 1.25,
                      overflow: "hidden",
                      border: selected
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${theme.palette.divider}`,
                      bgcolor: dark ? alpha("#fff", 0.04) : alpha("#000", 0.04),
                      transition: "transform 0.15s ease, border-color 0.15s ease",
                      "&:hover": { transform: "scale(1.03)" },
                      "&:focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt={item.name || ""}
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
            {visibleCount < items.length ? (
              <Box
                ref={sentinelRef}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 1.5,
                  color: "text.secondary",
                }}
              >
                <CircularProgress size={16} thickness={5} />
              </Box>
            ) : null}
          </>
        )}
      </Box>
    </Box>
  );
}

/**
 * Toggle-style speech recognition: stays on until the user turns it off.
 * Auto-restarts after silent pauses / browser end events.
 */
function useSpeechToText({ onResult }) {
  const [listening, setListening] = React.useState(false);
  const recRef = React.useRef(null);
  const wantListenRef = React.useRef(false);
  const onResultRef = React.useRef(onResult);
  onResultRef.current = onResult;

  const supported =
    typeof window !== "undefined" &&
    Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  const stop = React.useCallback(() => {
    wantListenRef.current = false;
    setListening(false);
    try {
      recRef.current?.stop?.();
    } catch {
      /* */
    }
    recRef.current = null;
  }, []);

  const beginSession = React.useCallback(() => {
    if (!supported || !wantListenRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    try {
      recRef.current?.stop?.();
    } catch {
      /* */
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || "en-US";
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let transcript = "";
      let isFinal = false;
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
        if (event.results[i].isFinal) isFinal = true;
      }
      if (transcript.trim()) onResultRef.current?.(transcript.trim(), isFinal);
    };

    rec.onerror = (event) => {
      const err = event?.error;
      // Keep mic on through silence / abort noise; only hard-stop on permission etc.
      if (err === "not-allowed" || err === "service-not-allowed") {
        wantListenRef.current = false;
        setListening(false);
        return;
      }
      if (err === "no-speech" || err === "aborted" || err === "network") {
        return;
      }
    };

    rec.onend = () => {
      if (wantListenRef.current) {
        // Browser often ends after a short silence — restart while still on.
        window.setTimeout(() => {
          if (wantListenRef.current) beginSession();
        }, 180);
      } else {
        setListening(false);
      }
    };

    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      window.setTimeout(() => {
        if (wantListenRef.current) beginSession();
      }, 320);
    }
  }, [supported]);

  const start = React.useCallback(() => {
    if (!supported) return;
    wantListenRef.current = true;
    setListening(true);
    beginSession();
  }, [beginSession, supported]);

  const toggle = React.useCallback(() => {
    if (wantListenRef.current) stop();
    else start();
  }, [start, stop]);

  React.useEffect(() => () => stop(), [stop]);

  return { supported, listening, start, stop, toggle };
}

function panelSx(theme) {
  const dark = theme.palette.mode === "dark";
  return {
    borderRadius: 3.5,
    bgcolor: dark ? alpha("#1a1a1a", 0.98) : theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.common.white, dark ? 0.1 : 0.08)}`,
    boxShadow: dark
      ? "0 16px 48px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.35)"
      : "0 16px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
    backdropFilter: "blur(12px)",
    overflow: "hidden",
  };
}

export default function GenerateImageView({
  lang,
  onBack,
  models: modelsProp,
  initialReference = null,
  studioHistory = [],
  onGalleryRefresh,
}) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const { hitAxios } = React.useContext(GlobalContext);
  const { getUserData } = React.useContext(UserContext);

  const models = React.useMemo(() => {
    const list = (modelsProp?.length ? modelsProp : POYO_TXT2IMG_MODELS).map((m) => ({
      id: m.id,
      label: cleanImageModelLabel(m.label || m.name || m.id),
      description: stripVendorNames(m.description || ""),
      tier: m.tier || "alt",
    }));
    return [
      {
        id: AUTO_MODEL_ID,
        label: lang?.imageStudioAutoModel || "Auto",
        description:
          lang?.imageStudioAutoModelHint ||
          "Picks the best latest image model for you.",
        tier: "best",
      },
      ...list,
    ];
  }, [modelsProp, lang]);

  const [prompt, setPrompt] = React.useState("");
  const [modelId, setModelId] = React.useState(AUTO_MODEL_ID);
  const [aspectRatio, setAspectRatio] = React.useState(DEFAULT_ASPECT_RATIO || "9:16");
  /** @type {[import('../imageStudioRefs').ImageStudioRef[], Function]} */
  const [references, setReferences] = React.useState([]);
  const [lightbox, setLightbox] = React.useState(null); // { url, prompt, rawUrl }
  const [plusAnchor, setPlusAnchor] = React.useState(null);
  const [modelAnchor, setModelAnchor] = React.useState(null);
  const [aspectAnchor, setAspectAnchor] = React.useState(null);
  const [modelQuery, setModelQuery] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [turns, setTurns] = React.useState([]);
  const [history, setHistory] = React.useState([]);
  const [error, setError] = React.useState("");
  const [copiedId, setCopiedId] = React.useState("");
  const [visibleChatCount, setVisibleChatCount] = React.useState(CHAT_PAGE_SIZE);
  const fileInputRef = React.useRef(null);
  const modelChipRef = React.useRef(null);
  const bottomRef = React.useRef(null);
  const chatScrollRef = React.useRef(null);
  const chatTopSentinelRef = React.useRef(null);
  const pendingScrollRestoreRef = React.useRef(null);
  const interimRef = React.useRef("");
  const seededRef = React.useRef(false);
  const turnElsRef = React.useRef(new Map());

  const visibleTurns = React.useMemo(() => {
    if (turns.length <= visibleChatCount) return turns;
    return turns.slice(turns.length - visibleChatCount);
  }, [turns, visibleChatCount]);

  const hasOlderChats = turns.length > visibleChatCount;

  const loadOlderChats = React.useCallback(() => {
    const root = chatScrollRef.current;
    if (root) {
      pendingScrollRestoreRef.current = {
        height: root.scrollHeight,
        top: root.scrollTop,
      };
    }
    setVisibleChatCount((n) => Math.min(n + CHAT_PAGE_SIZE, turns.length));
  }, [turns.length]);

  React.useLayoutEffect(() => {
    const root = chatScrollRef.current;
    const pending = pendingScrollRestoreRef.current;
    if (!root || !pending) return;
    pendingScrollRestoreRef.current = null;
    const delta = root.scrollHeight - pending.height;
    root.scrollTop = pending.top + delta;
  }, [visibleChatCount, visibleTurns.length]);

  React.useEffect(() => {
    const root = chatScrollRef.current;
    const sentinel = chatTopSentinelRef.current;
    if (!root || !sentinel || !hasOlderChats) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadOlderChats();
      },
      { root, rootMargin: "100px 0px 0px 0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasOlderChats, loadOlderChats, visibleChatCount]);

  const refreshHistory = React.useCallback(async () => {
    try {
      const res = await fetchInfModels(hitAxios, { force: true });
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      setHistory(
        rows.filter(
          (r) =>
            r.status === "active" &&
            (r.photo_url || r.photoUrl),
        ),
      );
    } catch {
      /* ignore */
    }
  }, [hitAxios]);

  React.useEffect(() => {
    const fromProp = Array.isArray(studioHistory)
      ? studioHistory.filter(
          (r) => r.status === "active" && (r.photo_url || r.photoUrl),
        )
      : [];
    if (fromProp.length) setHistory(fromProp);
    else refreshHistory();
    // only seed from gallery list once / when it changes length materially
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studioHistory?.length]);

  // Keep the chat feed filled with prompt + response pairs from gallery history.
  React.useEffect(() => {
    if (!history.length) return;
    setTurns((prev) => {
      const next = mergeTurnsWithHistory(prev, history);
      if (
        next.length === prev.length &&
        next.every(
          (t, i) =>
            t.id === prev[i].id &&
            t.status === prev[i].status &&
            t.imageUrl === prev[i].imageUrl &&
            t.prompt === prev[i].prompt &&
            t.jobId === prev[i].jobId,
        )
      ) {
        return prev;
      }
      return next;
    });
  }, [history]);

  React.useEffect(() => {
    if (!initialReference || seededRef.current) return;
    seededRef.current = true;
    const abs = absoluteMediaUrl(
      initialReference.referenceUrl || initialReference.previewUrl,
    );
    const preview =
      initialReference.previewUrl ||
      mediaSrc(initialReference.referenceUrl) ||
      abs;
    if (abs || preview) {
      setReferences([
        {
          id: `seed_ref_${initialReference.seedId || Date.now()}`,
          previewUrl: preview || abs,
          remoteUrl: abs || undefined,
          label: labelFromFilename(initialReference.name || "Reference", 0),
          name: initialReference.name || "Reference",
        },
      ]);
    }
    if (initialReference.mode === "edit") {
      setPrompt("");
    }
    if (preview) {
      const seedTurn = {
        id: `seed_${initialReference.seedId || Date.now()}`,
        prompt:
          initialReference.prompt ||
          initialReference.name ||
          lang?.imageStudioFromLibrary ||
          "From Image Studio",
        status: "done",
        imageUrl: preview,
        jobId: initialReference.seedId || null,
        error: null,
        fromHistory: Boolean(initialReference.seedId),
      };
      setTurns((prev) => {
        if (
          seedTurn.jobId &&
          prev.some((t) => Number(t.jobId) === Number(seedTurn.jobId))
        ) {
          return prev;
        }
        if (prev.some((t) => t.id === seedTurn.id || t.imageUrl === preview)) {
          return prev;
        }
        return [...prev, seedTurn];
      });
    }
  }, [initialReference, lang]);

  const onSpeech = React.useCallback((text, isFinal) => {
    if (isFinal) {
      setPrompt((prev) => {
        const base = interimRef.current
          ? prev.slice(0, Math.max(0, prev.length - interimRef.current.length)).trimEnd()
          : prev;
        interimRef.current = "";
        return `${base}${base ? " " : ""}${text}`.trim();
      });
    } else {
      setPrompt((prev) => {
        const base = interimRef.current
          ? prev.slice(0, Math.max(0, prev.length - interimRef.current.length)).trimEnd()
          : prev;
        interimRef.current = text;
        return `${base}${base ? " " : ""}${text}`.trim();
      });
    }
  }, []);

  const speech = useSpeechToText({ onResult: onSpeech });

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [turns, submitting]);

  const selectedModel = models.find((m) => m.id === modelId) || models[0];
  const modelChipLabel =
    modelId === AUTO_MODEL_ID
      ? lang?.imageStudioAutoModel || "Auto"
      : selectedModel?.label || "Model";

  const filteredModels = React.useMemo(() => {
    const q = modelQuery.trim().toLowerCase();
    if (!q) return models;
    return models.filter(
      (m) =>
        m.label.toLowerCase().includes(q) ||
        (m.description || "").toLowerCase().includes(q),
    );
  }, [models, modelQuery]);

  const mentionAssets = React.useMemo(
    () => collectStudioMentionAssets(references),
    [references],
  );

  const selectedRefUrls = React.useMemo(() => {
    const s = new Set();
    for (const r of references) {
      if (r.previewUrl) s.add(r.previewUrl);
      if (r.remoteUrl) s.add(r.remoteUrl);
    }
    return s;
  }, [references]);

  const resolveModelId = () =>
    modelId === AUTO_MODEL_ID ? AUTO_RESOLVES_TO : modelId || AUTO_RESOLVES_TO;

  const clearReferences = React.useCallback(() => {
    setReferences((prev) => {
      for (const r of prev) {
        if (r.previewUrl?.startsWith("blob:")) {
          try {
            URL.revokeObjectURL(r.previewUrl);
          } catch {
            /* */
          }
        }
      }
      return [];
    });
  }, []);

  const removeReference = (id) => {
    setReferences((prev) => {
      const target = prev.find((r) => r.id === id);
      if (target?.previewUrl?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(target.previewUrl);
        } catch {
          /* */
        }
      }
      return prev.filter((r) => r.id !== id);
    });
  };

  /** Add a reference (upload or gallery URL). Caps at MAX_IMAGE_STUDIO_REFS. */
  const addReference = ({ previewUrl, remoteUrl, file, name } = {}) => {
    const preview = previewUrl || remoteUrl || "";
    if (!preview) return;
    setReferences((prev) => {
      if (prev.length >= MAX_IMAGE_STUDIO_REFS) {
        setError(
          lang?.imageStudioMaxRefs ||
            `You can attach up to ${MAX_IMAGE_STUDIO_REFS} reference images.`,
        );
        return prev;
      }
      const remote = remoteUrl ? absoluteMediaUrl(remoteUrl) : "";
      if (remote && prev.some((r) => r.remoteUrl === remote)) return prev;
      if (preview && prev.some((r) => r.previewUrl === preview && !file)) return prev;
      const label = labelFromFilename(name || file?.name, prev.length);
      return [
        ...prev,
        {
          id: `ref_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          previewUrl: preview,
          remoteUrl: remote || undefined,
          file: file || undefined,
          label,
          name: name || file?.name || label,
        },
      ];
    });
    setError("");
  };

  /** Use a generated image as an additional reference. */
  const useAsReference = (imageUrl, { edit = false } = {}) => {
    const abs = absoluteMediaUrl(imageUrl);
    if (!abs) return;
    addReference({
      previewUrl: mediaSrc(imageUrl) || abs,
      remoteUrl: abs,
      name: "Reference",
    });
    setLightbox(null);
    if (edit) {
      setPrompt("");
      setError("");
    }
  };

  const onPickFiles = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const valid = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    for (const file of files) {
      if (!valid.includes(file.type)) {
        setError(lang?.invalidFileType || "Only JPG, PNG, or WebP allowed");
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError(lang?.fileSizeTooLarge || "Max file size is 5MB");
        continue;
      }
      addReference({
        file,
        previewUrl: URL.createObjectURL(file),
        name: file.name,
      });
    }
    setPlusAnchor(null);
  };

  const pollUntilDone = async (jobId) => {
    const id = Number(jobId);
    if (!Number.isFinite(id) || id < 1) {
      return { ok: false, msg: "Missing generation job id from server" };
    }
    const started = Date.now();
    let lastStatus = "";
    while (Date.now() - started < 8 * 60 * 1000) {
      await new Promise((r) => setTimeout(r, 4000));
      const res = await fetchInfModelStatus(hitAxios, id);
      if (res?.success === false && res?.logout) {
        return { ok: false, msg: "Session expired. Please sign in again." };
      }
      const row = res?.data?.data;
      if (!row || Number(row.id) !== id) {
        // Fallback for older backends without model_status.
        if (res?.data?.success === false) {
          const listRes = await fetchInfModels(hitAxios, { force: true });
          const rows = Array.isArray(listRes?.data?.data) ? listRes.data.data : [];
          const found = rows.find((r) => Number(r.id) === id);
          if (!found) {
            lastStatus = "missing";
            continue;
          }
          lastStatus = String(found.status || "");
          if (found.status === "active" && (found.photo_url || found.photoUrl)) {
            invalidateInfModelsCache();
            return { ok: true, row: found };
          }
          if (found.status === "error") {
            return {
              ok: false,
              msg: found.error_message || found.errorMessage || "Generation failed",
            };
          }
          continue;
        }
        lastStatus = "missing";
        continue;
      }
      lastStatus = String(row.status || "");
      if (row.status === "active" && (row.photo_url || row.photoUrl)) {
        invalidateInfModelsCache();
        return { ok: true, row };
      }
      if (row.status === "error") {
        return {
          ok: false,
          msg: row.error_message || row.errorMessage || "Generation failed",
        };
      }
    }
    return {
      ok: false,
      msg:
        lastStatus === "processing"
          ? "Still generating after several minutes. Check the gallery shortly, or try again."
          : "Timed out waiting for image",
    };
  };

  const submitGenerate = async () => {
    const text = prompt.trim();
    const refsSnapshot = [...references];
    if (!text && !refsSnapshot.length) {
      setError(lang?.pleaseEnterPrompt || "Enter a prompt or upload an image");
      return;
    }
    if (!text && refsSnapshot.length) {
      setError(
        lang?.imageStudioRefNeedsPrompt ||
          "Add a prompt describing how to use your reference image(s).",
      );
      return;
    }
    if (speech.listening) speech.stop();
    setError("");
    setSubmitting(true);

    const turnId = `t_${Date.now()}`;
    const displayPrompt = promptWithVisibleMentions(text);
    const apiPrompt = buildImageStudioApiPrompt(text, refsSnapshot);
    // Durable previews (data URL / https) so thumbs survive blob revoke after submit.
    const refPreviews = await durableReferencePreviews(refsSnapshot);

    setTurns((prev) => [
      ...prev,
      {
        id: turnId,
        prompt: displayPrompt,
        status: "processing",
        imageUrl: null,
        jobId: null,
        error: null,
        referencePreviews: refPreviews,
      },
    ]);

    try {
      let res;
      const mid = resolveModelId();
      const hasReference = refsSnapshot.length > 0;
      const fileRefs = refsSnapshot.filter((r) => r.file);
      const urlRefs = refsSnapshot
        .filter((r) => r.remoteUrl && !r.file)
        .map((r) => r.remoteUrl)
        .filter(Boolean);
      const b64ForApi = refPreviews.filter((u) => String(u).startsWith("data:image/"));

      if (hasReference) {
        const formData = new FormData();
        formData.append("creation_type", "prompt");
        formData.append("prompt", apiPrompt);
        formData.append(
          "name",
          autoNameFromPrompt(displayPrompt, hasReference ? "Edit" : "Image"),
        );
        formData.append("poyo_model", mid);
        formData.append("kie_model", mid);
        formData.append("aspect_ratio", aspectRatio || "9:16");
        formData.append("has_references", "1");
        formData.append(
          "client_reference_previews",
          JSON.stringify(refPreviews.slice(0, 14)),
        );
        if (b64ForApi.length) {
          formData.append("reference_images_b64", JSON.stringify(b64ForApi));
        }
        for (const r of fileRefs) {
          formData.append("photo", r.file);
        }
        if (urlRefs.length) {
          formData.append("reference_image_urls", JSON.stringify(urlRefs));
        }
        if (!b64ForApi.length && !fileRefs.length && !urlRefs.length) {
          throw new Error("Reference image missing — re-attach and try again.");
        }
        res = await hitAxios({
          path: "/api/inf/add_model",
          post: true,
          admin: false,
          obj: formData,
          showLoading: false,
          showSnackbar: false,
        });
      } else {
        res = await hitAxios({
          path: "/api/inf/add_model",
          post: true,
          admin: false,
          obj: {
            creation_type: "prompt",
            prompt: apiPrompt,
            name: autoNameFromPrompt(displayPrompt, "Image"),
            poyo_model: mid,
            kie_model: mid,
            aspect_ratio: aspectRatio || "9:16",
          },
          showLoading: false,
          showSnackbar: false,
        });
      }

      if (!res?.data?.success) {
        throw new Error(res?.data?.msg || "Could not start generation — check your connection and try again.");
      }

      const jobId = Number(res.data?.data?.id);
      if (!Number.isFinite(jobId) || jobId < 1) {
        throw new Error("Server did not return a job id. Generation may not have started.");
      }

      setTurns((prev) =>
        prev.map((t) => (t.id === turnId ? { ...t, jobId } : t)),
      );
      // Free the composer once the job is queued.
      setPrompt("");
      clearReferences();
      invalidateInfModelsCache();

      const done = await pollUntilDone(jobId);
      if (done.ok) {
        setTurns((prev) =>
          prev.map((t) =>
            t.id === turnId
              ? {
                  ...t,
                  status: "done",
                  imageUrl: mediaSrc(done.row.photo_url || done.row.photoUrl),
                }
              : t,
          ),
        );
      } else {
        setTurns((prev) =>
          prev.map((t) =>
            t.id === turnId
              ? { ...t, status: "error", error: done.msg || "Failed" }
              : t,
          ),
        );
        // Keep the failure on the turn only — avoid duplicating it above the composer.
        setError("");
      }

      getUserData?.();
      await refreshHistory();
      onGalleryRefresh?.();
    } catch (err) {
      const msg = err?.message || "Something went wrong";
      setError(msg);
      setTurns((prev) =>
        prev.map((t) =>
          t.id === turnId ? { ...t, status: "error", error: msg } : t,
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!submitting) submitGenerate();
    }
  };

  const copyPrompt = (text, turnId) => {
    const value = String(text || "");
    if (!value) return;
    navigator.clipboard?.writeText?.(value);
    if (turnId) {
      setCopiedId(turnId);
      window.setTimeout(() => setCopiedId(""), 1600);
    }
  };

  const downloadImage = async (url, name) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${String(name || "image").replace(/[^\w\-]+/g, "_")}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      window.open(url, "_blank");
    }
  };

  const openModelPicker = (anchorEl) => {
    setModelQuery("");
    setModelAnchor(anchorEl);
  };

  const selectHistoryItem = React.useCallback(
    (item, { url, abs }) => {
      useAsReference(abs || url, { edit: false });
      const existing = turnElsRef.current.get(Number(item.id));
      if (existing) {
        existing.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      setTurns((prev) => {
        if (prev.some((t) => t.jobId === item.id || t.imageUrl === url)) {
          return prev;
        }
        return [
          ...prev,
          {
            id: `hist_${item.id}`,
            prompt: item.prompt || item.name || "History",
            status: "done",
            imageUrl: url,
            jobId: item.id,
            error: null,
            fromHistory: true,
          },
        ];
      });
    },
    // useAsReference is stable enough in this component scope
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        gap: { xs: 1.5, md: 2 },
        height: { xs: "calc(100vh - 120px)", md: "calc(100vh - 140px)" },
        maxWidth: { xs: 880, md: 1280 },
        mx: "auto",
        position: "relative",
        px: { xs: 0.5, sm: 1 },
        overflow: "hidden",
        width: "100%",
      }}
    >
      {/* Main chat column */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          order: { xs: 1, md: 1 },
        }}
      >
      {/* Fixed top: title */}
      <Box sx={{ flexShrink: 0, pt: 0.5, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <IconButton
            onClick={onBack}
            size="small"
            sx={{
              color: "text.secondary",
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.08) },
            }}
          >
            <ArrowBackOutlined fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} letterSpacing={-0.2}>
              {lang?.imageStudioGenerateTitle || "Generate Image"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {references.length
                ? lang?.imageStudioEditingHint || "Editing with Image Studio reference"
                : "Prompt · model · aspect · create"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Scrollable middle only */}
      <Box
        ref={chatScrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          pr: 0.5,
          pb: 1,
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: alpha(theme.palette.text.primary, 0.18),
            borderRadius: 99,
          },
        }}
      >
        {turns.length === 0 && !submitting && (
          <Box
            sx={{
              py: { xs: 10, sm: 14 },
              textAlign: "center",
              animation: "fadeIn 0.5s ease",
              "@keyframes fadeIn": {
                from: { opacity: 0, transform: "translateY(8px)" },
                to: { opacity: 1, transform: "none" },
              },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2,
                borderRadius: "22px",
                display: "grid",
                placeItems: "center",
                background: dark
                  ? `linear-gradient(145deg, ${alpha("#fff", 0.08)}, ${alpha("#fff", 0.02)})`
                  : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.04)})`,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <AutoAwesomeOutlined sx={{ fontSize: 28, opacity: 0.75 }} />
            </Box>
            <Typography
              variant="h5"
              fontWeight={700}
              color="text.primary"
              gutterBottom
              sx={{ letterSpacing: -0.4 }}
            >
              {lang?.imageStudioEmptyTitle || "What do you want to create?"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: "auto" }}>
              {lang?.imageStudioEmptyHint ||
                "Describe an image, choose a model, set the aspect ratio, then generate."}
            </Typography>
          </Box>
        )}

        <Stack spacing={3.5}>
          {hasOlderChats ? (
            <Box
              ref={chatTopSentinelRef}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 1,
                color: "text.secondary",
              }}
            >
              <CircularProgress size={16} thickness={5} sx={{ mr: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {lang?.imageStudioLoadOlder || "Scroll for older chats"}
              </Typography>
            </Box>
          ) : null}
          {visibleTurns.map((turn) => (
            <Box
              key={turn.id}
              ref={(el) => {
                if (turn.jobId != null) {
                  if (el) turnElsRef.current.set(Number(turn.jobId), el);
                  else turnElsRef.current.delete(Number(turn.jobId));
                }
              }}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                animation: "turnIn 0.35s ease",
                "@keyframes turnIn": {
                  from: { opacity: 0, transform: "translateY(10px)" },
                  to: { opacity: 1, transform: "none" },
                },
              }}
            >
              {/* User prompt — right */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  width: "100%",
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "flex-start",
                    gap: 0.75,
                    maxWidth: { xs: "88%", sm: "78%" },
                    px: 2,
                    py: 1.25,
                    borderRadius: "18px 18px 6px 18px",
                    bgcolor: dark
                      ? alpha(theme.palette.primary.main, 0.18)
                      : alpha(theme.palette.primary.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.6,
                      flex: 1,
                      minWidth: 0,
                      maxHeight: 220,
                      overflowY: "auto",
                    }}
                  >
                    {turn.prompt}
                  </Typography>
                  <Tooltip
                    title={
                      copiedId === turn.id
                        ? lang?.copied || "Copied"
                        : lang?.copyPrompt || "Copy prompt"
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() => copyPrompt(turn.prompt, turn.id)}
                      sx={{
                        flexShrink: 0,
                        mt: -0.25,
                        color: copiedId === turn.id ? "success.main" : "text.secondary",
                      }}
                    >
                      {copiedId === turn.id ? (
                        <CheckRounded sx={{ fontSize: 18 }} />
                      ) : (
                        <ContentCopyOutlined sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>

                {Array.isArray(turn.referencePreviews) &&
                turn.referencePreviews.some((u) => u && !String(u).startsWith("blob:")) ? (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                    justifyContent="flex-end"
                    sx={{ maxWidth: { xs: "88%", sm: "78%" } }}
                  >
                    {turn.referencePreviews
                      .filter((u) => u && !String(u).startsWith("blob:"))
                      .map((src, i) => (
                      <Box
                        key={`${turn.id}_ref_${i}`}
                        sx={{
                          position: "relative",
                          width: 56,
                          height: 56,
                          borderRadius: 1.5,
                          overflow: "hidden",
                          border: `1px solid ${theme.palette.divider}`,
                          flexShrink: 0,
                          bgcolor: dark ? alpha("#fff", 0.06) : alpha("#000", 0.04),
                        }}
                      >
                        <Box
                          component="img"
                          src={src}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.visibility = "hidden";
                          }}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            position: "absolute",
                            left: 3,
                            bottom: 3,
                            px: 0.5,
                            borderRadius: 0.75,
                            bgcolor: "rgba(0,0,0,0.65)",
                            color: "#fff",
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            lineHeight: 1.4,
                          }}
                        >
                          Ref
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : null}
              </Box>

              {/* Model response — left */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%",
                }}
              >
                <Box sx={{ maxWidth: { xs: "92%", sm: "85%" } }}>
                  {turn.status === "processing" && (
                    <Box sx={{ py: 0.5 }}>
                      <ImageCreatingPlaceholder lang={lang} />
                    </Box>
                  )}

                  {turn.status === "error" && (
                    <Typography variant="body2" color="error.main" sx={{ py: 1 }}>
                      {turn.error}
                    </Typography>
                  )}

                  {turn.status === "done" && turn.imageUrl && (
                    <Box>
                      <Box
                        onClick={() =>
                          setLightbox({
                            url: turn.imageUrl,
                            prompt: turn.prompt,
                          })
                        }
                        sx={{
                          position: "relative",
                          display: "inline-block",
                          maxWidth: THUMB_MAX,
                          cursor: "zoom-in",
                          borderRadius: 2.5,
                          overflow: "hidden",
                          border: `1px solid ${theme.palette.divider}`,
                          boxShadow: dark
                            ? "0 8px 24px rgba(0,0,0,0.35)"
                            : "0 8px 20px rgba(0,0,0,0.08)",
                          "&:hover .thumb-zoom": { opacity: 1 },
                        }}
                      >
                        <Box
                          component="img"
                          src={turn.imageUrl}
                          alt={turn.prompt}
                          sx={{
                            display: "block",
                            width: "100%",
                            maxWidth: THUMB_MAX,
                            maxHeight: THUMB_MAX,
                            objectFit: "cover",
                            aspectRatio: "3 / 4",
                          }}
                        />
                        <Box
                          className="thumb-zoom"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: "rgba(0,0,0,0.28)",
                            opacity: 0,
                            transition: "opacity 0.2s ease",
                          }}
                        >
                          <ZoomInOutlined sx={{ color: "#fff", fontSize: 28 }} />
                        </Box>
                      </Box>
                      <Stack
                        direction="row"
                        spacing={0.25}
                        sx={{
                          mt: 0.75,
                          opacity: 0.75,
                          "&:hover": { opacity: 1 },
                          flexWrap: "wrap",
                        }}
                      >
                        <Tooltip title={lang?.imageStudioEdit || "Edit image"}>
                          <IconButton
                            size="small"
                            onClick={() => useAsReference(turn.imageUrl, { edit: true })}
                          >
                            <BrushOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={lang?.imageStudioUseReference || "Use as reference"}>
                          <IconButton
                            size="small"
                            onClick={() => useAsReference(turn.imageUrl)}
                          >
                            <ImageOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={lang?.copyPrompt || "Copy prompt"}>
                          <IconButton
                            size="small"
                            onClick={() => copyPrompt(turn.prompt, turn.id)}
                          >
                            <ContentCopyOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={lang?.characterDownload || "Download"}>
                          <IconButton
                            size="small"
                            onClick={() => downloadImage(turn.imageUrl, turn.prompt)}
                          >
                            <DownloadOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={lang?.regenerate || "Use prompt again"}>
                          <IconButton
                            size="small"
                            onClick={() => setPrompt(turn.prompt)}
                          >
                            <RefreshOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
        <div ref={bottomRef} />
      </Box>

      {/* Fixed bottom composer */}
      <Box
        data-tour="image-studio-composer"
        sx={{
          flexShrink: 0,
          pt: 1.25,
          pb: 1.25,
          background: theme.palette.background.default,
          borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
        }}
      >
        {error ? (
          <Typography
            variant="caption"
            color="error.main"
            sx={{ display: "block", mb: 1, px: 1.5 }}
          >
            {error}
          </Typography>
        ) : null}

        {references.length ? (
          <Box sx={{ px: 1.5, mb: 1.25, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {references.map((ref) => (
              <Box key={ref.id} sx={{ position: "relative", display: "inline-block" }}>
                <Box
                  component="img"
                  src={ref.previewUrl}
                  alt={ref.label || ""}
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: "cover",
                    borderRadius: 2,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.55)}`,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    left: 4,
                    bottom: 4,
                    px: 0.6,
                    py: 0.1,
                    borderRadius: 1,
                    bgcolor: "rgba(0,0,0,0.65)",
                    color: "#fff",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    maxWidth: 56,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {ref.label || "Ref"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => removeReference(ref.id)}
                  sx={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    bgcolor: dark ? "#2a2a2a" : "background.paper",
                    border: `1px solid ${theme.palette.divider}`,
                    width: 22,
                    height: 22,
                  }}
                >
                  <CloseOutlined sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            px: 1.25,
            pt: 1,
            pb: 1,
            borderRadius: 3.5,
            bgcolor: dark ? alpha("#fff", 0.06) : alpha("#000", 0.035),
            border: `1px solid ${alpha(theme.palette.text.primary, dark ? 0.12 : 0.08)}`,
            boxShadow: dark
              ? "0 10px 36px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)"
              : "0 8px 28px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)",
            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            "&:focus-within": {
              borderColor: alpha(theme.palette.primary.main, 0.45),
              boxShadow: dark
                ? `0 10px 36px rgba(0,0,0,0.5), 0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`
                : `0 8px 28px rgba(0,0,0,0.1), 0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0 }}>
            <IconButton
              size="small"
              onClick={(e) => setPlusAnchor(e.currentTarget)}
              aria-label={lang?.imageStudioAttach || "Attach"}
              data-tour="image-studio-attach"
              sx={{
                color: "text.primary",
                mt: 0.15,
                width: 36,
                height: 36,
                flexShrink: 0,
                bgcolor: Boolean(plusAnchor)
                  ? alpha(theme.palette.text.primary, 0.1)
                  : "transparent",
                "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.08) },
              }}
            >
              {plusAnchor ? <CloseOutlined fontSize="small" /> : <AddOutlined />}
            </IconButton>

            <Box sx={{ flex: 1, minWidth: 0, "& .MuiFormHelperText-root": { display: "none" } }}>
              <MentionPromptInput
                value={prompt}
                onChange={setPrompt}
                assets={mentionAssets}
                embedded
                placeholder={
                  references.length
                    ? lang?.imageStudioEditPlaceholder ||
                      "Describe what to create from your references — type @ to tag them…"
                    : lang?.imageStudioPromptPlaceholder ||
                      "Describe the image you want… Type @ after uploading references."
                }
                minRows={2}
                disabled={submitting}
                lang={lang}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.35,
              flexWrap: "wrap",
              pl: 5.5,
            }}
          >
            <Box
              component="button"
              type="button"
              ref={modelChipRef}
              data-model-chip
              data-tour="image-studio-model"
              onClick={(e) => openModelPicker(e.currentTarget)}
              sx={{
                all: "unset",
                cursor: "pointer",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.35,
                borderRadius: 999,
                px: 1.15,
                py: 0.55,
                fontSize: "0.8125rem",
                fontWeight: 700,
                letterSpacing: -0.1,
                bgcolor: alpha(theme.palette.text.primary, dark ? 0.1 : 0.06),
                color: "text.primary",
                border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                transition: "background 0.15s ease, transform 0.12s ease",
                "&:hover": {
                  bgcolor: alpha(theme.palette.text.primary, dark ? 0.14 : 0.09),
                },
                "&:active": { transform: "scale(0.97)" },
              }}
            >
              {modelChipLabel}
              <KeyboardArrowDownRounded sx={{ fontSize: 16, opacity: 0.65 }} />
            </Box>

            <Tooltip title={`${lang?.characterAspectRatio || "Aspect ratio"} · ${aspectRatio}`}>
              <IconButton
                size="small"
                data-tour="image-studio-settings"
                onClick={(e) => setAspectAnchor(e.currentTarget)}
                sx={{
                  color: "text.secondary",
                  "&:hover": { color: "text.primary" },
                }}
              >
                <SettingsOutlined fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip
              title={
                speech.supported
                  ? speech.listening
                    ? lang?.stopListening || "Stop listening"
                    : lang?.voiceToText || "Voice to text"
                  : lang?.voiceNotSupported || "Voice input not supported in this browser"
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!speech.supported}
                  onClick={speech.toggle}
                  sx={{
                    color: speech.listening ? "error.main" : "text.secondary",
                    bgcolor: speech.listening
                      ? alpha(theme.palette.error.main, 0.12)
                      : "transparent",
                    animation: speech.listening ? `${pulseGlow} 1.6s ease infinite` : "none",
                    "&:hover": {
                      color: speech.listening ? "error.light" : "text.primary",
                      bgcolor: speech.listening
                        ? alpha(theme.palette.error.main, 0.18)
                        : alpha(theme.palette.text.primary, 0.06),
                    },
                  }}
                >
                  {speech.supported ? (
                    <MicNoneOutlined fontSize="small" />
                  ) : (
                    <MicOffOutlined fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <IconButton
              disabled={submitting || !prompt.trim()}
              onClick={submitGenerate}
              data-tour="image-studio-generate"
              sx={{
                width: 38,
                height: 38,
                bgcolor: dark ? "#f5f5f5" : theme.palette.text.primary,
                color: dark ? "#111" : theme.palette.background.default,
                transition: "transform 0.15s ease, opacity 0.15s ease",
                "&:hover": {
                  bgcolor: dark ? "#fff" : theme.palette.text.primary,
                  opacity: 0.92,
                },
                "&:active": { transform: "scale(0.94)" },
                "&.Mui-disabled": {
                  bgcolor: alpha(theme.palette.text.primary, 0.12),
                  color: "text.disabled",
                },
              }}
            >
              {submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendRounded sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: "block", textAlign: "center", mt: 1, letterSpacing: 0.2 }}
        >
          {aspectRatio}
          {" · "}
          {modelId === AUTO_MODEL_ID
            ? lang?.imageStudioAutoModel || "Auto"
            : modelChipLabel}
          {references.length ? ` · ${references.length} reference${references.length > 1 ? "s" : ""}` : ""}
          {speech.listening ? " · Listening…" : ""}
        </Typography>
      </Box>
      </Box>

      {/* Right history rail */}
      <Box
        data-tour="image-studio-history"
        sx={{
          width: { xs: "100%", md: 380 },
          flexShrink: 0,
          height: { xs: 240, md: "100%" },
          minHeight: { xs: 220, md: 0 },
          order: { xs: 2, md: 2 },
          alignSelf: "stretch",
        }}
      >
        <GenerationHistoryPanel
          history={history}
          lang={lang}
          theme={theme}
          dark={dark}
          selectedUrls={selectedRefUrls}
          onSelect={selectHistoryItem}
        />
      </Box>

      {/* Portals / overlays — keep out of the main flex flow */}
      <Box sx={{ position: "fixed", width: 0, height: 0, overflow: "hidden" }} aria-hidden>

      {/* Gemini-style + attach menu */}
      <Menu
        anchorEl={plusAnchor}
        open={Boolean(plusAnchor)}
        onClose={() => setPlusAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        TransitionProps={{ timeout: 180 }}
        slotProps={{
          paper: {
            sx: {
              ...panelSx(theme),
              minWidth: 260,
              py: 1,
              mt: -1,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setPlusAnchor(null);
            fileInputRef.current?.click();
          }}
          sx={{
            py: 1.35,
            px: 2,
            mx: 0.5,
            borderRadius: 2,
            gap: 0.5,
            "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.06) },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: "text.primary" }}>
            <AttachFileOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={lang?.imageStudioUploadTitle || "Upload references"}
            secondary={
              lang?.imageStudioUploadHint ||
              `Attach up to ${MAX_IMAGE_STUDIO_REFS} images · type @ to tag in prompt`
            }
            primaryTypographyProps={{ fontWeight: 500, fontSize: "0.9375rem" }}
            secondaryTypographyProps={{ fontSize: "0.75rem" }}
          />
        </MenuItem>
        <Divider sx={{ my: 0.75, mx: 1.5, borderColor: alpha(theme.palette.text.primary, 0.08) }} />
        <MenuItem
          onClick={() => {
            setPlusAnchor(null);
            window.setTimeout(() => openModelPicker(modelChipRef.current), 60);
          }}
          sx={{
            py: 1.35,
            px: 2,
            mx: 0.5,
            borderRadius: 2,
            "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.06) },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: "text.primary" }}>
            <AutoAwesomeOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={lang?.characterAiModel || "Choose model"}
            secondary={modelChipLabel}
            primaryTypographyProps={{ fontWeight: 500, fontSize: "0.9375rem" }}
            secondaryTypographyProps={{ fontSize: "0.75rem" }}
          />
        </MenuItem>
      </Menu>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept="image/jpeg,image/png,image/webp,image/jpg"
        multiple
        onChange={(e) => {
          onPickFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {/* Model search panel */}
      <Popover
        open={Boolean(modelAnchor)}
        anchorEl={modelAnchor}
        onClose={() => setModelAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionProps={{ timeout: 160 }}
        slotProps={{
          paper: {
            sx: {
              ...panelSx(theme),
              width: 340,
              maxWidth: "92vw",
              mt: -1.25,
            },
          },
        }}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={modelQuery}
            onChange={(e) => setModelQuery(e.target.value)}
            placeholder={lang?.searchPlaceholder || "Search..."}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.text.primary, dark ? 0.05 : 0.03),
                "& fieldset": { borderColor: alpha(theme.palette.text.primary, 0.08) },
              },
            }}
          />
        </Box>
        <Box sx={{ maxHeight: 300, overflowY: "auto", px: 0.75, pb: 1 }}>
          {filteredModels.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 2, textAlign: "center" }}
            >
              No models found
            </Typography>
          )}
          {filteredModels.map((option) => {
            const selected = option.id === modelId;
            return (
              <Box
                key={option.id}
                role="option"
                aria-selected={selected}
                onClick={() => {
                  setModelId(option.id);
                  setModelAnchor(null);
                }}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  px: 1.5,
                  py: 1.15,
                  mx: 0.25,
                  mb: 0.25,
                  borderRadius: 2.25,
                  cursor: "pointer",
                  bgcolor: selected
                    ? alpha(theme.palette.text.primary, dark ? 0.1 : 0.06)
                    : "transparent",
                  transition: "background 0.12s ease",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.text.primary, dark ? 0.08 : 0.05),
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {option.label}
                  </Typography>
                  {option.description ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: 1.35,
                      }}
                    >
                      {option.description}
                    </Typography>
                  ) : null}
                </Box>
                {selected ? (
                  <CheckRounded sx={{ fontSize: 18, mt: 0.25, opacity: 0.8 }} />
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Popover>

      {/* Aspect menu */}
      <Menu
        anchorEl={aspectAnchor}
        open={Boolean(aspectAnchor)}
        onClose={() => setAspectAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              ...panelSx(theme),
              minWidth: 200,
              py: 0.75,
              mt: -1,
            },
          },
        }}
      >
        {(POYO_ASPECT_RATIOS || []).map((opt) => (
          <MenuItem
            key={opt.id}
            selected={aspectRatio === opt.id}
            onClick={() => {
              setAspectRatio(opt.id);
              setAspectAnchor(null);
            }}
            sx={{
              py: 1.15,
              px: 1.75,
              mx: 0.5,
              borderRadius: 2,
              "&.Mui-selected": {
                bgcolor: alpha(theme.palette.text.primary, 0.08),
              },
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" width="100%">
              <AspectIcon ratio={opt.id} selected={aspectRatio === opt.id} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={700}>
                  {opt.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {opt.hint}
                </Typography>
              </Box>
              {aspectRatio === opt.id ? (
                <CheckRounded sx={{ fontSize: 16, opacity: 0.7 }} />
              ) : null}
            </Stack>
          </MenuItem>
        ))}
      </Menu>

      {/* Full-size lightbox with edit actions */}
      <Modal
        open={Boolean(lightbox)}
        onClose={() => setLightbox(null)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 250,
            sx: { bgcolor: "rgba(0,0,0,0.88)" },
          },
        }}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
      >
        <Fade in={Boolean(lightbox)} timeout={250}>
          <Box
            sx={{
              outline: "none",
              position: "relative",
              maxWidth: "min(920px, 94vw)",
              maxHeight: "92vh",
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: dark ? "#121212" : "#111",
              border: `1px solid ${alpha("#fff", 0.1)}`,
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 2,
                bgcolor: "rgba(0,0,0,0.45)",
                borderRadius: 999,
                p: 0.35,
              }}
            >
              <Tooltip title={lang?.imageStudioEdit || "Edit image"}>
                <IconButton
                  size="small"
                  sx={{ color: "#fff" }}
                  onClick={() =>
                    useAsReference(lightbox?.url, { edit: true })
                  }
                >
                  <BrushOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={lang?.imageStudioUseReference || "Use as reference"}>
                <IconButton
                  size="small"
                  sx={{ color: "#fff" }}
                  onClick={() => useAsReference(lightbox?.url)}
                >
                  <ImageOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={lang?.characterDownload || "Download"}>
                <IconButton
                  size="small"
                  sx={{ color: "#fff" }}
                  onClick={() => downloadImage(lightbox?.url, lightbox?.prompt)}
                >
                  <DownloadOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                onClick={() => setLightbox(null)}
              >
                <CloseOutlined fontSize="small" />
              </IconButton>
            </Stack>

            <Box
              component="img"
              src={lightbox?.url}
              alt={lightbox?.prompt || ""}
              sx={{
                display: "block",
                maxWidth: "min(920px, 94vw)",
                maxHeight: "72vh",
                objectFit: "contain",
                mx: "auto",
              }}
            />

            <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${alpha("#fff", 0.08)}` }}>
              <Typography
                variant="body2"
                sx={{
                  color: alpha("#fff", 0.85),
                  mb: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {lightbox?.prompt}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<BrushOutlined />}
                  onClick={() => useAsReference(lightbox?.url, { edit: true })}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    bgcolor: "#f5f5f5",
                    color: "#111",
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  {lang?.imageStudioEdit || "Edit image"}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ImageOutlined />}
                  onClick={() => useAsReference(lightbox?.url)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    color: "#fff",
                    borderColor: alpha("#fff", 0.28),
                    "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.06) },
                  }}
                >
                  {lang?.imageStudioUseReference || "Use as reference"}
                </Button>
              </Stack>
              <Typography variant="caption" sx={{ color: alpha("#fff", 0.45), display: "block", mt: 1.25 }}>
                {lang?.imageStudioEditHint ||
                  "Describe character or feature changes in the prompt bar — keep the same people, swap outfits, restyle, or rewrite text."}
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Modal>
      </Box>
    </Box>
  );
}
